// © Microsoft Corporation. All rights reserved.

import { AccentButton, LightweightButton } from "@microsoft/fast-components-react-msft";
import { CommentTelemetryObjects, commentTelemetryContracts } from "./Comment.telemetry";
import { TelemetryEventMap, addOrUpdateTelemetryObject, dispatchTelemetryEvent, renderHybrid } from "../Social.telemetry";
import avatarFallback, { contentImageFallback } from "../utils/avatarFallback";
import { chevron_left, close2, comment } from "../utils/icons";
import { hasThemeBase, isComponentDisabled } from "../utils/themeHelpers";

import CommentList from "./comment-list/CommentList";
import { Content } from "@msnews/social-data-service";
import ContentPanel from "./content-panel/ContentPanel";
import DisabledMessage from "./disabled-message/DisabledMessage";
import NotificationTray from "../common/notification/NotificationTray";
import React from "react";
import { SocialProps } from "../Social.props";
import { SocialState } from "../Social.state";
import UserCard from "./user-card/UserCard";
import { classNames } from "@microsoft/fast-web-utilities";
import { commentsSectionIndicatorExist } from "../utils/urlHelper";
import { connect } from "react-redux";
import { enums } from "../utils/enums";
import { getContextData } from "@msnews/core";
import isValidURL from "../utils/isValidURL";
import loadObserver from "../utils/ObserverHelper";
import { placeholderImages } from "../utils/images";
import { selectContentId } from "../utils/contentIdKey";

interface CommentState {
    commentsLoaded: boolean;
    showNotificationTray: boolean;
    userCardVisible: boolean;
    userCardUserId: string;
    displayState: number;
    isOverlayActive: boolean; // Toogle for swiping comments widget on flight-3
    isCompactFloatButtonVisible: boolean;
    isEntireComponentScrolled: boolean;
    buttonVisibilityTurnedOn: boolean;
    componentIsInView: boolean;
}

export interface CommentProps extends SocialProps {
    content: Content;
}

const mapStateToProps = function (state: SocialState, props: SocialProps) {
    const contentId = selectContentId(state.contentIds, props.id, props.market);
    return {
        responsiveClass: state.responsiveClass,
        content: state.contents && state.contents[contentId]
    };
};

export class Comment extends React.Component<CommentProps, CommentState> {
    commentContainerRef: React.RefObject<HTMLDivElement>;
    commentBox: HTMLDivElement;
    minimalContainerRef: React.RefObject<HTMLDivElement>;
    minimalContainerObserver: IntersectionObserver;
    telemetryObjects: CommentTelemetryObjects;
    overlayObserver: IntersectionObserver;
    overlayContainerRef: HTMLDivElement;
    componentEndIndicatorRef: React.RefObject<HTMLDivElement>;
    isContainerLoaded: boolean;

    constructor(props) {
        super(props);
        this.commentContainerRef = React.createRef();
        this.minimalContainerRef = React.createRef();
        this.componentEndIndicatorRef = React.createRef();
        loadObserver(() => {
            this.minimalContainerObserver = new IntersectionObserver(
                this.commentContainerIntersectionHandler.bind(this),
                { threshold: [0, .2, .4, .6, .8, 1]}
            );
        });

        // Overlay Comments Container
        this.overlayContainerRef = null;
        loadObserver(() => {
            this.overlayObserver = new IntersectionObserver(
                () => this.overlayContainerRef && dispatchTelemetryEvent({...TelemetryEventMap.genericLoaded, sendPeregrine: true}, this.overlayContainerRef.parentElement.parentElement),
                { threshold: .8}
            );
        });

        this.state = {
            commentsLoaded: false,
            showNotificationTray: !isComponentDisabled(props.theme, "notificationtray"),
            userCardVisible: false,
            userCardUserId: null,
            displayState: enums.commentsStatusType.notDetermined,
            isOverlayActive: false,
            isCompactFloatButtonVisible: true,
            isEntireComponentScrolled: false,
            buttonVisibilityTurnedOn: true,
            componentIsInView: false
        };
    }

    componentDidMount() {
        document.addEventListener("cwt-comments-clicked", (e) => this.toggleCommentsState(e));
        document.addEventListener("scroll", this.trackScrolling.bind(this));
        this.prepareCommentConnector();
    }

    prepareCommentConnector() {
        const commentToBeFetchedCount = this.props.commentDisplayType == "overlay" ? 6 : 2;
        this.props.commentConnector && this.props.commentConnector.onCommentComponentConnect(
            { id: this.props.id, market: this.props.market, top: commentToBeFetchedCount },
            this.props.specificCommentId
        );
    }

    componentDidUpdate(prevProps) {
        if (this.props.id != null && this.props.market != null && prevProps.id != this.props.id || prevProps.market != this.props.market || prevProps.commentDisplayType != this.props.commentDisplayType || prevProps.specificCommentId != this.props.specificCommentId) {
            this.prepareCommentConnector();
            this.setState({
                commentsLoaded: false,
                displayState: enums.commentsStatusType.notDetermined
            });
        }

        const { content, specificCommentId } = this.props;
        const { displayState, commentsLoaded } = this.state;
        if (content && !commentsLoaded) {
            if (specificCommentId) {
                this.expandComponent();
            } else if (this.props.commentDisplayType && this.props.commentDisplayType.indexOf("compact") == 0) {
                this.setState({
                    displayState: content.totalCommentCount > 0 ? enums.commentsStatusType.minimal : enums.commentsStatusType.zero
                });
            } else {
                this.expandComponent();
            }
            if (displayState !== enums.commentsStatusType.notDetermined) {
                this.setState({ commentsLoaded: true });
                if ((commentsSectionIndicatorExist() || specificCommentId)) {
                    this.setState({displayState: enums.commentsStatusType.expanded}, () => {
                        this.setFloatingButtonVisibility(false);
                        const isDefault = this.props.commentDisplayType == "default"; // overlay and compact themes are handled onCommentBoxInitialized
                        const isOverlay = this.props.commentDisplayType === "overlay";
                        if (isDefault) { // setTimeout's are necessary to provide smooth transition on initial loading
                            setTimeout(() => { this.jumpToCommentsSection(); }, 250);
                        } else if (isOverlay) {
                            setTimeout(() => { this.setState({isOverlayActive: true}); }, 250);
                        }
                    });
                }
            }
        }

        // Observe Comments Container
        if (!this.isContainerLoaded && this.minimalContainerRef && this.minimalContainerRef.current) {
            this.isContainerLoaded = true;
            this.minimalContainerObserver.observe(this.minimalContainerRef.current);
        }

        // Observe Overlay Comments Container
        if (!this.isContainerLoaded && this.overlayContainerRef) {
            this.isContainerLoaded = true;
            this.overlayObserver.observe(this.overlayContainerRef);
        }
    }

    componentWillUnmount() {
        this.isContainerLoaded = false;
        document.removeEventListener("scroll", this.trackScrolling.bind(this));

        // Unobserve Comments Container
        if (this.minimalContainerRef && this.minimalContainerRef.current) {
            this.minimalContainerObserver.unobserve(this.minimalContainerRef.current);
        }

        // Unobserve Overlay Comments Container
        if (this.overlayContainerRef) {
            this.overlayObserver.unobserve(this.overlayContainerRef);
        }
    }

    render() {
        if (this.state.displayState == enums.commentsStatusType.notDetermined) { return null; }

        const { me, managedClasses, usersConnector, users, market, responsiveClass, t, content, mainTelemetryObject, requestContext, commentDisplayType, specificCommentId, componentConnector, theme} = this.props;
        if (!content) { return null; }

        const { commentStatus, url, commentWhiteListSegments, commentBlackListSegments } = content;
        if (commentStatus == "Off") { return null; }
        if (!isValidURL(getContextData(requestContext.data.locationHref, "locationHref"), url, commentWhiteListSegments, commentBlackListSegments)) { return null; }

        const isMobile = this.props.responsiveClass.includes(enums.responsiveLevels.MobilePopup);
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        const avatarUrl = me && (me.state != enums.userStateType.newUser && me.avatarStatus == enums.userAvatarStatusType.normal ? me.avatarUrl || placeholderImages.avatar : placeholderImages.avatar);
        const isExpanded = this.isComponentExpanded();
        const contentDisabled = commentStatus != "On";

        const renderParams = { me, managedClasses, usersConnector, users, market, responsiveClass, t, content, isSuperApp, isExpanded, contentDisabled, avatarUrl, isMobile, mainTelemetryObject, componentConnector, theme };
        const isOverlay = commentDisplayType === "overlay";
        const commmentView =  isOverlay ? this.renderOverlay(renderParams) : this.renderDefaultOrCompact(renderParams);

        const notificationPosition = isOverlay ? "left" : "right";

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            compactStartConversationButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentTelemetryContracts.compactStartConversationButton}, { id: content.cmsId }, this.telemetryObjects && this.telemetryObjects.compactStartConversationButton),
            compactJoinConversationButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentTelemetryContracts.compactJoinConversationButton}, { id: content.cmsId }, this.telemetryObjects && this.telemetryObjects.compactJoinConversationButton),
            overlayAddCommentButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentTelemetryContracts.overlayAddCommentButton}, { id: content.cmsId }, this.telemetryObjects && this.telemetryObjects.overlayAddCommentButton),
            overlayStartConversationButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentTelemetryContracts.overlayStartConversationButton}, { id: content.cmsId }, this.telemetryObjects && this.telemetryObjects.overlayStartConversationButton),
            overlayJoinConversationButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentTelemetryContracts.overlayJoinConversationButton}, { id: content.cmsId }, this.telemetryObjects && this.telemetryObjects.overlayJoinConversationButton),
            mobileFloaterButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentTelemetryContracts.mobileFloaterButton}, { id: content.cmsId }, this.telemetryObjects && this.telemetryObjects.mobileFloaterButton),
            overlayComments: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentTelemetryContracts.overlayComments}, { id: content.cmsId }, this.telemetryObjects && this.telemetryObjects.overlayComments)
        };

        return <React.Fragment>
            { commmentView }
            { this.state.showNotificationTray && <NotificationTray {...{...this.props, alignment: notificationPosition}}  /> }
            <div ref={this.componentEndIndicatorRef} id="component-end-indicator"></div>
        </React.Fragment>;
    }

    renderContentPanel() {
        const { commentDisplayType, managedClasses, popupConnector, commentConnector, t, contents, content, mainTelemetryObject, responsiveClass, market, componentConnector, theme } = this.props;
        const isExpanded = this.isComponentExpanded();
        const props = { commentDisplayType, content, t, managedClasses, popupConnector, commentConnector, contents, responsiveClass, isExpanded, mainTelemetryObject, market, componentConnector, theme };
        return <ContentPanel {...props} />;
    }

    renderCommentList() {
        const { componentConnector, commentConnector, responsiveClass, me, t, managedClasses, usersConnector, popupConnector, users, market, content, mainTelemetryObject, theme, commentDisplayType, specificCommentId } = this.props;
        const { commentStatus, comments, id: contentId, totalCommentCount } = content;
        const contentDisabled = commentStatus != "On";
        const isExpanded = this.isComponentExpanded();
        const isInZeroState = this.state.displayState == enums.commentsStatusType.zero;
        const displayNotInitialized = this.state.displayState == enums.commentsStatusType.notDetermined;
        const onCommentBoxInitialized = this.onCommentBoxInitialized.bind(this);

        if (!contentDisabled && !isInZeroState && !displayNotInitialized) {
            return <CommentList {...{ onCommentBoxInitialized, commentDisplayType, isExpanded, componentConnector, responsiveClass, comments, totalCommentCount, contentId, contentDisabled, me, t, managedClasses, commentConnector, usersConnector, popupConnector, users, market, content, mainTelemetryObject, theme, specificCommentId }} />;
        }
    }

    handleKeyDown(e) {
        if (e.keyCode == 9) {
            const target = window.document.getElementById(`joinButton_${this.props.content.id}`);
            if (target != document.activeElement) {
                if (target) {
                    target.focus();
                    e.stopPropagation();
                    e.preventDefault();
                }
            }
        }
    }

    renderDefaultOrCompact(renderParams) {
        const {t, managedClasses, responsiveClass, isSuperApp, isExpanded, contentDisabled } = renderParams;
        const isCompactFloatMode = this.props.commentDisplayType === "compactFloat";
        const isMobile = this.props.responsiveClass.includes(enums.responsiveLevels.MobilePopup);
        const isZeroState = this.state.displayState == enums.commentsStatusType.zero;
        const buttonName = isZeroState ? t.CommentStartConversation : t.CommentJoinConversation;

        return (
            <div
                className={classNames(
                    managedClasses.commentContainer,
                    [managedClasses.commentSuperAppContainer, isSuperApp],
                    [managedClasses.commentMinimalContainer, this.state.displayState == enums.commentsStatusType.minimal],
                    [managedClasses.commentZeroStateContainer, this.state.displayState == enums.commentsStatusType.zero],
                    responsiveClass
                )}
                tabIndex={ this.isComponentExpanded() ? -1 : 0}
                aria-label={buttonName}
                ref={!isCompactFloatMode ? this.commentContainerRef : this.minimalContainerRef}
                onClick={(e) => !isExpanded && this.compactCommentsClickHandler(e)}
                onKeyDown={(e) => this.handleKeyDown(e)}
            >
                { contentDisabled
                    ? this.renderContentDisabledSection(renderParams)
                    : <React.Fragment>
                        { this.renderContentPanel() }
                        { this.renderCommentList() }
                        { <div className="cwt-join-container"> {this.renderDefaultCTAButton()} </div> }

                        { isCompactFloatMode && !this.isComponentExpanded() &&
                            (isMobile
                                ? this.renderOverlayCTAButton()
                                : <div className={classNames(
                                    "compact-floating-button",
                                    [managedClasses.buttonFadeInContainer, this.state.buttonVisibilityTurnedOn],
                                    [managedClasses.buttonFadeOutContainer, !this.state.buttonVisibilityTurnedOn]
                                )}> {this.renderDefaultCTAButton()} </div>
                            )
                        }
                    </React.Fragment>
                }
            </div>
        );
    }

    renderOverlay(renderParams) {
        const { managedClasses, responsiveClass, content, contentDisabled, isMobile } = renderParams;
        const contentThumbnailUrl = content.thumbnail ? content.thumbnail : placeholderImages.content;
        return (
            <div
                className={classNames(
                    managedClasses.commentContainer,
                    managedClasses.commentOverlayContainer,
                    [managedClasses.commentOverlayActiveContainer, this.state.isOverlayActive],
                    responsiveClass
                )}
                ref={this.commentContainerRef}
            >
                { contentDisabled
                    ? this.renderContentDisabledSection(renderParams)
                    : <React.Fragment>
                        { !this.state.isOverlayActive && !this.isComponentScrolledDown() && this.renderOverlayCTAButton() }

                        { this.state.isOverlayActive &&
                            <React.Fragment>
                                { !isMobile &&
                                    <React.Fragment>
                                        { this.renderContentPanel() }
                                        <LightweightButton onClick={(e) => this.hideOverlayHandler(e)} className="cwt-close-button" title = "Close">
                                            <i className="cwt-icon"> {close2} </i>
                                        </LightweightButton>
                                    </React.Fragment>
                                }

                                { isMobile &&
                                    <div className="overlay-title-container">
                                        <LightweightButton onClick={(e) => this.hideOverlayHandler(e)} className="cwt-fast-button cwt-navbar-button" >
                                            <i className="cwt-icon"> {chevron_left} </i>
                                        </LightweightButton>
                                        <div className="cwt-image-container">
                                            <img width="40" height="40" alt="Content thumbnail" className="cwt-content-image" src={contentThumbnailUrl} onError={contentImageFallback}/>
                                        </div>
                                        <span className="cwt-title">{content.title}</span>
                                    </div>
                                }

                                <div {...renderHybrid(this.telemetryObjects && this.telemetryObjects.overlayComments, true, (el) => { this.overlayContainerRef = el; })}>
                                    {this.renderCommentList()}
                                </div>
                            </React.Fragment>
                        }
                    </React.Fragment>
                }
            </div>
        );
    }

    renderContentDisabledSection(renderParams) {
        const { me, avatarUrl, usersConnector, users, market, managedClasses, t, componentConnector, theme } = renderParams;
        return (
            <div className="cwt-content-disabled-container">
                {me &&
                    <div className="cwt-avatar-container" >
                        <img
                            alt={t.AriaLabelsProfilePicture}
                            className="cwt-link cwt-commentbox-avatar"
                            src={avatarUrl}
                            onError={(e)=>{ avatarFallback(e); }}
                            onMouseEnter={(e) => this.showUserCard(e, me.id)}
                        />
                    </div>
                }
                {this.state.userCardVisible &&
                    <UserCard
                        {...{usersConnector, users, market, me, managedClasses, t, id: this.state.userCardUserId, visible: true,
                            onHide: () => this.setState({userCardVisible: false}),
                            triggerRef: this.commentContainerRef,
                            isHover: true,
                            mainTelemetryObject: this.props.mainTelemetryObject
                        }}
                    />
                }
                <div className="cwt-col-stretch">
                    <DisabledMessage {...{ managedClasses, showGuidelines: true, message: t.ContentCommentsDisabledMsg, t, componentConnector, theme, market }} />
                </div>
            </div>
        );
    }

    renderDefaultCTAButton() {
        if (this.isComponentExpanded()) { return; }

        const { t, content } = this.props;
        const { totalCommentCount } = content || {};
        const isZeroState = this.state.displayState == enums.commentsStatusType.zero;
        const buttonName = isZeroState ? t.CommentStartConversation : t.CommentJoinConversation;
        const buttonText = isZeroState ? t.CommentStartConversation : `${t.CommentJoinConversation}${totalCommentCount && totalCommentCount > 0 && ` (${totalCommentCount}) `}`;
        const telemetryObject = this.telemetryObjects && (isZeroState ? this.telemetryObjects.compactStartConversationButton : this.telemetryObjects.compactJoinConversationButton);

        const showCommentsFloatButton = (
            <AccentButton
                onClick={(e) => this.showCompactCommentsHandler(e, telemetryObject)}
                id={`joinButton_${content.id}`}
                className="cwt-fast-button cwt-compact-cta-button"
                aria-live="assertive"
                type="button"
                tabIndex={1}
                name={buttonName}
                {...renderHybrid(telemetryObject, true)}
            >
                <i className="cwt-icon">
                    <span className="cwt-icon-label"> {buttonText} </span>
                    {comment}
                </i>
            </AccentButton>
        );

        return <div>{showCommentsFloatButton}</div>;
    }

    renderOverlayCTAButton() {
        const isMobile = this.props.responsiveClass.includes(enums.responsiveLevels.MobilePopup);
        if (isMobile && this.state.isOverlayActive) { return; }

        const { t, content, me, managedClasses } = this.props;
        const { totalCommentCount } = content || {};
        const isExistingUser = me && me.state && me.state != enums.userStateType.anonymous;
        const avatarUrl = me && (me.state != enums.userStateType.newUser && me.avatarStatus == enums.userAvatarStatusType.normal ? me.avatarUrl || placeholderImages.avatar : placeholderImages.avatar);
        const hasComment = totalCommentCount > 0;
        const showAddCommentMessage = !isMobile && hasComment && isExistingUser;
        const showProfilePicture = !isMobile && isExistingUser;
        const commentsCountLabel = totalCommentCount && <span className="cwt-icon-label"> {totalCommentCount} </span>;
        const isOverlay = this.props.commentDisplayType == "overlay";

        // Decide Button Message
        let buttonMessage = t.CommentStartConversation;
        if (hasComment) { buttonMessage = isExistingUser ? t.CommentAddComment : t.CommentJoinConversation; }

        // Decide Telemetry Object
        let telemetryObject;
        if (this.telemetryObjects) {
            if (isMobile) {
                telemetryObject = this.telemetryObjects.mobileFloaterButton;
            } else if (showAddCommentMessage) {
                telemetryObject = this.telemetryObjects.overlayAddCommentButton;
            } else if (hasComment) {
                telemetryObject = this.telemetryObjects.overlayJoinConversationButton;
            } else {
                telemetryObject = this.telemetryObjects.overlayStartConversationButton;
            }
        }

        const renderProfileImg = () =>
            showProfilePicture &&
            <div className="cwt-avatar-container">
                <img width="32" height="32" alt="user picture" className="cwt-avatar" src={avatarUrl} onError={(e)=>{ avatarFallback(e); }}/>
            </div>;

        const renderButtonIcon = () => <i className="cwt-icon"> {comment} {hasComment && commentsCountLabel} </i>;

        const showCommentsButton = <AccentButton
            onClick={(e) => this.showOverlayCommentsHandler(e, telemetryObject)}
            className={classNames(
                "cwt-fast-button",
                "cwt-overlay-cta-button",
                ["cwt-overlay-cta-button-desktop", !isMobile],
                [managedClasses.buttonFadeInContainer, this.state.buttonVisibilityTurnedOn && isMobile && !isOverlay],
                [managedClasses.buttonFadeOutContainer, !this.state.buttonVisibilityTurnedOn && isMobile && !isOverlay]
            )}
            beforeContent={renderProfileImg}
            afterContent={renderButtonIcon}
            aria-live="assertive"
            type="button"
            name={t.CommentJoinConversation}
            {...renderHybrid(telemetryObject, true)}
        >
            { !isMobile && <div className="cwt-title"> {buttonMessage} </div> }
        </AccentButton>;

        return <div> {showCommentsButton} </div>;
    }

    expandComponent() {
        this.setState({
            displayState: enums.commentsStatusType.expanded
        });
    }

    showUserCard(e, userId) {
        const me = this.props.me;
        if (me.state == enums.userStateType.anonymous) { return; }
        this.setState({userCardVisible: true, userCardUserId: userId});
    }

    compactCommentsClickHandler(e) {
        this.expandComponent();
    }

    showCompactCommentsHandler(e, telemetryObject?) {
        this.expandComponent();
        this.jumpToCommentsSection();
    }

    showOverlayCommentsHandler(e, telemetryObject?) {
        this.setState({isOverlayActive: true}, () => this.jumpToCommentsSection());
    }

    jumpToCommentsSection() {
        this.addCommentsHashToUrl();
        this.scrollCommentContainerIntoView();
        this.focusToCommentBox();
    }

    addCommentsHashToUrl() {
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        if (!isSuperApp && !document.location.href.includes("#comments")) {
            document.location.href = document.location.href.replace("#comments", "")+"#comments";
        }
    }

    hideOverlayHandler(e) {
        this.setState({isOverlayActive: false});
    }

    toggleCommentsState(e) {
        const isOverlay = this.props.commentDisplayType == "overlay";
        if (isOverlay) {
            const isOverlayActive = this.state.isOverlayActive;
            return isOverlayActive ? this.hideOverlayHandler(e) : this.showOverlayCommentsHandler(e);
        } else {
            if (!this.isComponentExpanded()) { this.expandComponent(); }
            this.jumpToCommentsSection();
        }
    }

    commentContainerIntersectionHandler(entries) {
        this.setState({componentIsInView: entries[0].intersectionRatio >= .1});
        this.minimalContainerRef && this.minimalContainerRef.current && dispatchTelemetryEvent({...TelemetryEventMap.genericLoaded, sendPeregrine: true}, this.minimalContainerRef.current);
    }

    onCommentBoxInitialized(commentBox: HTMLDivElement) {
        this.commentBox = commentBox;
        const isOverlayOrCompact = this.props.commentDisplayType == "overlay" || this.props.commentDisplayType.includes("compact");
        if (isOverlayOrCompact) {
            setTimeout(() => {
                this.jumpToCommentsSection();
            }, 250);
        }
    }

    trackScrolling() {
        const isEntireComponentScrolled = this.isComponentScrolledDown();
        const showButton = !this.state.componentIsInView && !isEntireComponentScrolled;
        this.setFloatingButtonVisibility(showButton);
    }

    isComponentScrolledDown() {
        return this.componentEndIndicatorRef.current ? this.componentEndIndicatorRef.current.getBoundingClientRect().top < 250 : false;
    }

    focusToCommentBox() {
        return this.commentBox && this.commentBox.focus({preventScroll: true});
    }

    isCommentContainerExist() {
        return !!((this.commentContainerRef && this.commentContainerRef.current) || (this.minimalContainerRef && this.minimalContainerRef.current));
    }

    scrollCommentContainerIntoView() {
        const containerExist = this.isCommentContainerExist();
        return containerExist && (this.commentContainerRef.current || this.minimalContainerRef.current).scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    }

    isComponentExpanded() {
        return this.state.displayState == enums.commentsStatusType.expanded;
    }

    setFloatingButtonVisibility(turnOn: boolean) {
        if (turnOn) {
            this.setState({isCompactFloatButtonVisible: true, buttonVisibilityTurnedOn: true});
        } else {
            this.setState({buttonVisibilityTurnedOn: false}, () => setTimeout(() => { this.setState({isCompactFloatButtonVisible: false}); }, 350));
        }
    }
}
export default connect(mapStateToProps)(Comment);